import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Building2, Loader2, Package, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import { ExternalCompanyPicker } from "@/components/shared/ExternalCompanyPicker";
import { useCreateProject } from "../hooks/useProjects";
import type { ProjectStatus } from "../types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  companyId?: string;
  productId?: string;
  /** Pre-fill the External client picker — opened from a client/customer
   *  External company's Projects tab. */
  defaultClientCompanyId?: string;
};

export function NewProjectDrawer({
  isOpen,
  onClose,
  companyId: prefilledCompanyId,
  productId: prefilledProductId,
  defaultClientCompanyId,
}: Props) {
  const createProject = useCreateProject();

  const [name, setName] = useState("");
  const [type, setType] = useState<string>("");
  const [status, setStatus] = useState<ProjectStatus>("PLANNING");
  const [description, setDescription] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState(prefilledCompanyId ?? "");
  const [selectedProductId, setSelectedProductId] = useState(prefilledProductId ?? "");
  const [selectedClientCompanyId, setSelectedClientCompanyId] = useState(
    defaultClientCompanyId ?? "",
  );
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [managerId, setManagerId] = useState("");
  const [progress, setProgress] = useState(0);

  // Reset prefilled values when props change
  useEffect(() => {
    setSelectedCompanyId(prefilledCompanyId ?? "");
    setSelectedProductId(prefilledProductId ?? "");
    setSelectedClientCompanyId(defaultClientCompanyId ?? "");
  }, [prefilledCompanyId, prefilledProductId, defaultClientCompanyId]);

  const { data: companiesResult } = useCompanyList({ page: 1, limit: 100, operationalOnly: true });
  const companies = companiesResult?.data ?? [];

  const { data: productsResult } = useProductList({
    page: 1,
    limit: 100,
    companyId: selectedCompanyId || undefined,
  });
  const products = productsResult?.data ?? [];

  const { data: usersResult } = useUserList({ page: 1, limit: 100 });
  const allUsers = usersResult?.data ?? [];

  const { data: projectTypeOptions = [] } = useCategoryItems("project_type");
  const activeTypeOptions = projectTypeOptions.filter((t) => t.isActive);

  // When dropdown values arrive on first open, default to the first active
  // value so the form doesn't sit on an empty Type.
  useEffect(() => {
    if (!isOpen) return;
    if (type) return;
    if (activeTypeOptions.length > 0) setType(activeTypeOptions[0].value);
  }, [isOpen, activeTypeOptions, type]);

  // When product is selected, auto-fill company
  useEffect(() => {
    if (selectedProductId) {
      const product = products.find((p) => p.id === selectedProductId);
      if (product?.companyId) {
        setSelectedCompanyId(product.companyId);
      }
    }
  }, [selectedProductId, products]);

  const companyLocked = !!prefilledCompanyId || !!selectedProductId;
  const productLocked = !!prefilledProductId;

  function reset() {
    setName("");
    setType(activeTypeOptions[0]?.value ?? "");
    setStatus("PLANNING");
    setDescription("");
    setSelectedCompanyId(prefilledCompanyId ?? "");
    setSelectedProductId(prefilledProductId ?? "");
    setSelectedClientCompanyId(defaultClientCompanyId ?? "");
    setStartDate("");
    setEndDate("");
    setManagerId("");
    setProgress(0);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    if (!selectedCompanyId && !selectedProductId) return;

    await createProject.mutateAsync({
      name: name.trim(),
      type: type || null,
      status,
      description: description.trim() || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      progress,
      companyId: selectedCompanyId || undefined,
      productId: selectedProductId || undefined,
      clientCompanyId: selectedClientCompanyId || null,
      managerId: managerId || undefined,
    });
    reset();
    onClose();
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] bg-black/30"
        onClick={onClose}
      />
      {/* Drawer */}
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <h2 className="text-lg font-semibold text-foreground">New Project</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col overflow-y-auto"
        >
          <div className="flex-1 space-y-6 p-6">
            {/* Project Details */}
            <h3 className="text-sm font-semibold text-foreground">
              Project Details
            </h3>

            <div className="space-y-2">
              <Label htmlFor="project-name">
                Project Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="project-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter project name"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="project-type">Project Type</Label>
                <Select
                  id="project-type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                >
                  {activeTypeOptions.length === 0 && (
                    <option value="">— No types configured —</option>
                  )}
                  {activeTypeOptions.length > 0 && !type && (
                    <option value="">— Select type —</option>
                  )}
                  {activeTypeOptions.map((t) => (
                    <option key={t.id} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-status">Status</Label>
                <Select
                  id="project-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as ProjectStatus)}
                >
                  <option value="PLANNING">Planning</option>
                  <option value="ACTIVE">Active</option>
                  <option value="ON_HOLD">On Hold</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="project-description">Description</Label>
              <textarea
                id="project-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Project description (optional)"
              />
            </div>

            {/* Association */}
            <h3 className="text-sm font-semibold text-foreground">
              Association
            </h3>

            <div className="space-y-2">
              <Label htmlFor="project-company">
                Company {!selectedProductId && <span className="text-destructive">*</span>}
              </Label>
              <div className="relative">
                <Building2
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="project-company"
                  value={selectedCompanyId}
                  onChange={(e) => {
                    setSelectedCompanyId(e.target.value);
                    if (!prefilledProductId) setSelectedProductId("");
                  }}
                  disabled={companyLocked}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">Select a company...</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="project-product">Product</Label>
              <div className="relative">
                <Package
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="project-product"
                  value={selectedProductId}
                  onChange={(e) => setSelectedProductId(e.target.value)}
                  disabled={productLocked}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">No product (direct under company)</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Select a product if this project is under a specific product.
                Otherwise it will be directly under the company.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="project-client">Client (External Company)</Label>
              <ExternalCompanyPicker
                id="project-client"
                value={selectedClientCompanyId}
                onChange={setSelectedClientCompanyId}
                systemCodes={["client", "customer"]}
                placeholder="No external client"
              />
              <p className="text-[11px] text-muted-foreground">
                Filtered to External companies with type=Client/Customer.
              </p>
            </div>

            {/* Dates & Management */}
            <h3 className="text-sm font-semibold text-foreground">
              Dates & Management
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="project-start-date">Start Date</Label>
                <Input
                  id="project-start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-end-date">End Date</Label>
                <Input
                  id="project-end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="project-manager">Manager</Label>
              <select
                id="project-manager"
                value={managerId}
                onChange={(e) => setManagerId(e.target.value)}
                className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Select a manager...</option>
                {allUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="project-progress">Progress ({progress}%)</Label>
              <input
                id="project-progress"
                type="range"
                min={0}
                max={100}
                value={progress}
                onChange={(e) => setProgress(Number(e.target.value))}
                className="w-full accent-primary"
              />
            </div>
          </div>

          {/* Footer */}
          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !name.trim() ||
                (!selectedCompanyId && !selectedProductId) ||
                createProject.isPending
              }
            >
              {createProject.isPending && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
              )}
              Create Project
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
